import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchClients,
  fetchSearchResults,
  fetchSearchHistory,
  fetchCurrentUserProfile,
  fetchUserProfile,
  fetchProviderBase,
  createSearch,
  fetchFilteredProviders,
  saveSearchResults,
  type SearchRequest,
  type SearchResultsRequest,
  type HistoryRequest,
  type FilteredProvidersRequest,
  type SaveResultsRequest,
  type ClientsResponse,
  type SearchResultsResponse,
  type HistoryResponse,
  type UserProfile,
  type ProviderBase,
} from '../api/matchmakingApi';

/**
 * Feature 036: Accept optional userRole parameter for role-based client fetching
 * Includes role in cache key to prevent cache conflicts between FA and SP
 */
export const useClients = (userRole?: string) => {
  return useQuery<ClientsResponse>({
    queryKey: ['matchmaking', 'clients', userRole],
    queryFn: () => fetchClients(userRole),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useSearchResults = (params: SearchResultsRequest | null) => {
  return useQuery<SearchResultsResponse>({
    queryKey: ['matchmaking', 'results', params?.searchId, params?.recordStart, params?.recordEnd],
    queryFn: () => fetchSearchResults(params!),
    enabled: !!params && !!params.searchId && !!params.advisorId && !!params.clientId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

export const useSearchHistory = (params: HistoryRequest | null) => {
  return useQuery<HistoryResponse>({
    queryKey: ['matchmaking', 'history', params?.advisorId, params?.clientId],
    queryFn: () => fetchSearchHistory(params!),
    enabled: !!params && !!params.advisorId && !!params.clientId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

export const useCurrentUserProfile = () => {
  return useQuery<UserProfile>({
    queryKey: ['user', 'profile', 'me'],
    queryFn: fetchCurrentUserProfile,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useUserProfile = (userId: string | null) => {
  return useQuery<UserProfile>({
    queryKey: ['user', 'profile', userId],
    queryFn: () => fetchUserProfile(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useProviderBase = (providerId: string | null) => {
  return useQuery<ProviderBase>({
    queryKey: ['provider', 'base', providerId],
    queryFn: () => fetchProviderBase(providerId!),
    enabled: !!providerId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useCreateSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SearchRequest) => createSearch(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matchmaking', 'history'] });
    },
  });
};

export const useFilteredProviders = () => {
  return useMutation({
    mutationFn: (params: FilteredProvidersRequest) => fetchFilteredProviders(params),
  });
};

export const useSaveSearchResults = () => {
  return useMutation({
    mutationFn: (params: SaveResultsRequest) => saveSearchResults(params),
  });
};

